import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useLazyCreateWalletQuery,
  useLazyDeployWalletQuery,
  useLazyFetchWalletTransactionsQuery,
  useLazyGetWalletQuery,
  useLazyWalletPinQuery,
  useLazyWalletStateQuery,
  useSignMessageMutation,
  useTransferMutation,
  useValidateAddressMutation,
} from '../api/wallet.api';
import { WalletAC, setWallet, setWalletModal } from '../store/wallet.slice';
import {
  FetchWalletTransactionsPayload,
  IWalletChallenge,
  IWalletModal,
  SignMessagePayload,
  ValidateAddressPayload,
  WalletPayload,
  WalletPinPayload,
  WalletStatePayload,
  WalletTransferPayload,
} from '../types/wallet.types';

interface UseWallet {
  getWallet: (payload: WalletPayload) => Promise<void>;
  fetchWalletTransactions: (payload: FetchWalletTransactionsPayload) => Promise<boolean>;
  validateAddress: (payload: ValidateAddressPayload) => Promise<boolean>;
  transfer: (payload: WalletTransferPayload) => Promise<IWalletChallenge | null>;
  openWalletModal: (payload: IWalletModal) => void;
  closeWalletModal: () => void;
  createWallet: () => Promise<IWalletChallenge | null>;
  signMessage: (payload: SignMessagePayload) => Promise<IWalletChallenge | null>;
  deployWallet: (payload: WalletPayload) => Promise<IWalletChallenge | null>;
  walletPin: (payload: WalletPinPayload) => Promise<IWalletChallenge | null>;
  walletState: (payload: WalletStatePayload) => Promise<boolean>;
}

export const useWallet = (): UseWallet => {
  const dispatch = useAppDispatch();

  const [getWalletQuery] = useLazyGetWalletQuery();
  const [fetchWalletTransactionsQuery] = useLazyFetchWalletTransactionsQuery();
  const [validateAddressQuery] = useValidateAddressMutation();
  const [transferQuery] = useTransferMutation();
  const [createWalletQuery] = useLazyCreateWalletQuery();
  const [signMessageQuery] = useSignMessageMutation();

  const [deployWalletQuery] = useLazyDeployWalletQuery();
  const [walletPinQuery] = useLazyWalletPinQuery();
  const [walletStateQuery] = useLazyWalletStateQuery();

  const walletState = async (payload: WalletStatePayload) => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await walletStateQuery(payload);
    dispatch(WalletAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update wallet');
      return false;
    }
    dispatch(setWallet(response.data));
    return true;
  };

  const walletPin = async (payload: WalletPinPayload) => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await walletPinQuery(payload);
    dispatch(WalletAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update wallet pin');
      return null;
    }
    return response.data;
  };

  const deployWallet = async (payload: WalletPayload) => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await deployWalletQuery(payload);
    dispatch(WalletAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to deploy wallet');
      return null;
    }
    return response.data;
  };

  const signMessage = async (payload: SignMessagePayload) => {
    const { data: response, error } = await signMessageQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to sign message');
      return null;
    }
    return response.data;
  };

  const createWallet = async () => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await createWalletQuery();

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to create Wallet');
      dispatch(WalletAC.toggleLoading(false));
      return null;
    }

    return response.data;
  };

  const getWallet = async (payload: WalletPayload) => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await getWalletQuery(payload);
    dispatch(WalletAC.toggleLoading(false));

    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to load Wallet info');
      return;
    }

    dispatch(setWallet(response.data));
  };

  const validateAddress = async (payload: ValidateAddressPayload) => {
    const { data: response, error } = await validateAddressQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to validate address');
      return false;
    }
    return response.data.is_valid;
  };

  const transfer = async (payload: WalletTransferPayload) => {
    const { data: response, error } = await transferQuery(payload);
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to transfer funds');
      return null;
    }

    return response.data;
  };

  const fetchWalletTransactions = async (payload: FetchWalletTransactionsPayload) => {
    dispatch(WalletAC.toggleLoading(true));
    const { data: response, error } = await fetchWalletTransactionsQuery(payload);
    dispatch(WalletAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Wallet Transactions!');
    } else {
      dispatch(WalletAC.clearItems());
      dispatch(WalletAC.setItems(response.data));
      dispatch(WalletAC.setPaginationMeta(response.meta));
    }

    return !isError;
  };

  const openWalletModal = (modal: IWalletModal) => {
    dispatch(setWalletModal(modal));
  };

  const closeWalletModal = () => {
    dispatch(setWalletModal(null));
  };

  return {
    getWallet,
    fetchWalletTransactions,
    validateAddress,
    transfer,
    openWalletModal,
    closeWalletModal,
    createWallet,
    signMessage,
    deployWallet,
    walletPin,
    walletState,
  };
};
